import React, { useEffect, useState } from "react";
import Steps from "../../Steps";
import Button from "../../../../common/Button";
import ChartNav from "../../../../common/ChartNav";
import Medications from "../../medications/medications";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { updateMedicationReview } from "../../../../../redux/modules/clinical-services/actions";
import CustomInput from "../../../../common/CustomInput";
import ReferralCreate from "./ReferralCreate";
import ConfirmationModalNew from "../../../../common/ConfirmationModalNew";
import SubNavigation from "../../common/sub-navigation";
import { formatDateToYYYYMMDD } from "../../../../../helpers/functions";
import {
  AMRReferralTypeOptions,
  AMRNotificationTypeOptions,
  notificationMethodOptions,
} from "./data";
import NotificationCreate from "./NotificationCreate";
import ReferralCard from "./referral-info-card";
import NotificationCard from "./notification-info-card";
import { getValidStep } from "../../../text/data";
import {
  buildNotificationDocumentationFields,
  buildPrimaryProviderPayload,
  buildReferralNotificationCardGroups,
  getNotificationCopyDocOverrides,
} from "./referrals-shared";

const ReferralsAMR = () => {
  const { clinicalServiceObj, clinicalServiceName, clinicalServiceSteps } =
    useSelector((state) => state.clinicalService);
  const { patient } = useSelector((state) => state.patients);
  const dispatch = useDispatch();
  const { serviceId } = useParams();
  const { currentStep, currentSubStep, documentation } =
    clinicalServiceObj.initialAssessment.initialAssessmentForm;
  const selectedPrimaryProvider =
    patient?.careProvider?.find((provider) => provider?.primaryProvider) ||
    patient?.careProvider?.[0] ||
    null;

  // helper to resolve notification defaults consistently (date picker expects YYYY-MM-DD)
  const resolveNotificationDefaults = (doc, types) => {
    if (types && types.includes("nonApplicable")) return { date: null, method: null };
    const raw = doc?.dateOfNotification;
    const hasStoredDate = raw != null && String(raw).trim() !== "";
    const date = hasStoredDate
      ? formatDateToYYYYMMDD(raw)
      : formatDateToYYYYMMDD(new Date());
    const method = doc?.methodOfNotification ? doc.methodOfNotification : "fax";
    return { date, method };
  };

  const toArray = (value) => (Array.isArray(value) ? value : []);
  const hasPrimaryProviderInfo = (items = []) =>
    toArray(items).some((item) => item?.referralType === "Primary Provider");
  const getDocCollections = (doc) => {
    const normalizedDoc = doc || {};
    return {
      doc: normalizedDoc,
      referralTypes: toArray(normalizedDoc.referralType),
      notificationTypes: toArray(normalizedDoc.notificationType),
      referralInfos: toArray(normalizedDoc.referralInformations),
      notificationInfos: toArray(normalizedDoc.notificationInformations),
    };
  };
  //referral constants
  const [referralTypes, setReferralTypes] = useState([]);
  const [referralInfo, setReferralInfo] = useState(null);
  const [createReferral, setCreateReferral] = useState(true);
  const [showReferralSlider, setShowReferralSlider] = useState(false);
  const [newReferralType, setNewReferralType] = useState(null);

  //notification constants
  const [notificationOptions, setNotificationOptions] = useState(
    AMRNotificationTypeOptions
  );
  const [notificationTypes, setNotificationTypes] = useState([]);
  const [notificationDate, setNotificationDate] = useState(() =>
    formatDateToYYYYMMDD(new Date())
  );
  const [notificationMethod, setnotificationMethod] = useState("fax");
  const [notificationInfo, setNotificationInfo] = useState(null);
  const [createNotification, setCreateNotification] = useState(true);
  const [showNotificationSlider, setShowNotificationSlider] = useState(false);
  const [newNotificationType, setNewNotificationType] = useState(null);

  
  const [openreferralDeleteDialog, setOpenreferralDeleteDialog] =
    useState(false);
  const [
    openNotificationConfirmationDialog,
    setOpenNotificationConfirmationDialog,
  ] = useState(false);
  const [openNotificationDeleteDialog, setOpenNotificationDeleteDialog] =
    useState(false);

  const [subStepComplete, setSubStepComplete] = useState(false);
  const [isDocHydrated, setIsDocHydrated] = useState(false);

  const buildUpdatedSteps = ({
    nextStep = currentStep,
    nextSubStep = currentSubStep,
    includeServiceSteps = false,
    documentationPatch = {},
  } = {}) => ({
    currentStep: nextStep,
    currentSubStep: nextSubStep,
    ...(includeServiceSteps ? { serviceSteps: clinicalServiceSteps } : {}),
    documentation: {
      ...documentation,
      ...documentationPatch,
    },
  });

  const persistMedicationReview = (
    updatedSteps,
    { localOnly = false, showToast = false } = {}
  ) => {
    dispatch({ type: "UPDATE_MEDICATION_REVIEW_LOCAL", payload: updatedSteps });
    if (!localOnly) {
      dispatch(updateMedicationReview(serviceId, updatedSteps, showToast));
    }
  };

  const onNext = () => {
    if (currentStep <= clinicalServiceSteps.length) {
      const { nextStep, nextSubStep } = getValidStep(
        clinicalServiceSteps,
        currentStep,
        currentSubStep,
        1
      );
      const updatedSteps = buildUpdatedSteps({
        nextStep,
        nextSubStep,
        includeServiceSteps: true,
        documentationPatch: {
          referralType: referralTypes,
          notificationType: notificationTypes,
          dateOfNotification: notificationDate,
          methodOfNotification: notificationMethod,
        },
      });
      persistMedicationReview(updatedSteps);
    }
  };

  const saveNotificationInformation = (
    info,
    newType,
    isUpdate = false,
    selectedTypeOverride = null,
    docFieldOverrides = null
  ) => {
    const docFields = buildNotificationDocumentationFields({
      info,
      newType,
      selectedTypeOverride,
      docFieldOverrides,
      notificationDate: notificationDate,
      methodOfNotification: notificationMethod,
    });

    const updatedSteps = buildUpdatedSteps({
      documentationPatch: docFields,
    });
    setNotificationTypes(docFields.notificationType);
    persistMedicationReview(updatedSteps, { showToast: isUpdate });
  };

  const saveReferralInformation = (
    info,
    isUpdate = false,
    unselectedRef = null
  ) => {
    //if no referral selected then info is null
    let updatedReferalls = [];
    let updatedReferallInfo = [];

    if (info !== null) {
      //if we have information object to save or update
      if (isUpdate) {
        updatedReferalls = [
          ...documentation.referralInformations
            .filter((x) => x.referralId !== info.referralId)
            .map((s) => s.referralType),
          info.referralType,
        ];
        updatedReferallInfo = documentation?.referralInformations
          ? [
              ...documentation.referralInformations.filter(
                (x) => x.referralId !== info.referralId
              ),
              info,
            ]
          : [info];
      } else {
        updatedReferalls = referralTypes.includes(info.referralType)
          ? referralTypes.filter((x) => x !== "No Referral")
          : [
              ...referralTypes.filter((x) => x !== "No Referral"),
              info.referralType,
            ];
        updatedReferallInfo = documentation?.referralInformations
          ? [...documentation.referralInformations, info]
          : [info];
      }
    } else {
      //if we have un seleceted any referral type or selected No Referral
      if (unselectedRef === null) {
        updatedReferalls = ["No Referral"];
        updatedReferallInfo = [];
      } else if (unselectedRef !== null) {
        updatedReferalls = referralTypes.filter((x) => x !== unselectedRef);
        updatedReferallInfo = documentation.referralInformations.filter(
          (x) => x.referralType !== unselectedRef
        );
      }
    }

    const sameAsReferralSelected = notificationTypes.includes("same");
    const noReferralSelected = updatedReferalls.includes("No Referral");
    const syncedNotificationTypes =
      sameAsReferralSelected && noReferralSelected ? ["nonApplicable"] : notificationTypes;
    const syncedNotificationInfos =
      sameAsReferralSelected && !noReferralSelected
        ? [...updatedReferallInfo]
        : sameAsReferralSelected && noReferralSelected
          ? []
          : documentation?.notificationInformations;

    const updatedSteps = buildUpdatedSteps({
      documentationPatch: {
        referralType: updatedReferalls,
        referralInformations: updatedReferallInfo,
        ...(sameAsReferralSelected
          ? {
              notificationType: syncedNotificationTypes,
              notificationInformations: syncedNotificationInfos,
            }
          : {}),
        dateOfNotification: notificationDate,
        methodOfNotification: notificationMethod,
      },
    });
    // Update local component state so UI matches the payload immediately
    setReferralTypes(updatedReferalls);
    if (sameAsReferralSelected) {
      setNotificationTypes(syncedNotificationTypes);
    }
    persistMedicationReview(updatedSteps, { showToast: isUpdate });
  };

  const onPrevious = () => {
    const { nextStep, nextSubStep } = getValidStep(
      clinicalServiceSteps,
      currentStep,
      currentSubStep,
      -1
    );
    const updatedSteps = buildUpdatedSteps({
      nextStep,
      nextSubStep,
      includeServiceSteps: true,
      documentationPatch: {
        referralType: referralTypes,
        notificationType: notificationTypes,
        dateOfNotification: notificationDate,
        methodOfNotification: notificationMethod,
      },
    });
    persistMedicationReview(updatedSteps);
  };

  //useefect section

  useEffect(() => {
    const { date, method } = resolveNotificationDefaults(documentation, notificationTypes);
    setNotificationDate(date);
    setnotificationMethod(method);
  }, [notificationTypes, documentation]);

  useEffect(() => {
    setNotificationOptions(
      referralTypes.includes("No Referral")
        ? AMRNotificationTypeOptions.filter(
            (x) => x.label !== "Same as Referral Type"
          )
        : AMRNotificationTypeOptions
    );
  }, [referralTypes]);

  useEffect(() => {
    const referralInfos = documentation?.referralInformations || [];
    const notificationInfos = documentation?.notificationInformations || [];

    const referralsValid =
      (referralTypes && referralTypes.includes("No Referral")) ||
      referralInfos.length > 0;

    const notificationsValid =
      (notificationTypes && notificationTypes.includes("nonApplicable")) ||
      notificationInfos.length > 0 ||
      ((notificationTypes && notificationTypes.length > 0) && notificationDate && notificationMethod);

    setSubStepComplete(Boolean(referralsValid && notificationsValid));
  }, [referralTypes, notificationTypes, documentation, notificationDate, notificationMethod]);

  const HandleReferralSelection = (value) => {
    setNewReferralType(value);
    //if ref type is not already selected
    if (!referralTypes.includes(value)) {
      if (value === "No Referral") {
        // remove all referral infos and keep No Referral
        setReferralTypes(["No Referral"]);
        saveReferralInformation(null);
      } else if (
        value === "Primary Provider" &&
        selectedPrimaryProvider
      ) {
        useExistingProvider({ seedReferral: true });
      } else {
        //add new referral
        setReferralTypes([...referralTypes, value]);
        setCreateReferral(true);
        setShowReferralSlider(true);
      }
    } else {
      //confirm to remove already selected referral info
      setReferralTypes(referralTypes.filter((x) => x !== value));
      saveReferralInformation(null, false, value);
    }
  };

  const HandleNotificationSelection = (values) => {
    const selectedValues = Array.isArray(values) ? values : [];
    const sameNewlySelected =
      selectedValues.includes("same") && !notificationTypes.includes("same");

    // "same" is valid only as a standalone selection.
    // If user picks another option afterward, drop "same" and continue.
    if (sameNewlySelected && (selectedValues.length === 1 || selectedValues.includes("nonApplicable"))) {
      const todayYmd = formatDateToYYYYMMDD(new Date());
      const referralInfos = documentation?.referralInformations || [];
      const updatedSteps = {
        currentStep,
        currentSubStep,
        documentation: {
          ...documentation,
          notificationType: ["same"],
          notificationInformations: referralInfos,
          dateOfNotification: todayYmd,
          methodOfNotification: notificationMethod,
        },
      };
      setNewNotificationType("same");
      setNotificationTypes(["same"]);
      setNotificationDate(todayYmd);
      persistMedicationReview(updatedSteps);
      return;
    }

    const normalizedValues = selectedValues.filter((value) => value !== "same");

    // find the differences to get selected value from multi select dropdown
    const difference1 = notificationTypes.filter(
      (item) => !normalizedValues.includes(item)
    );
    const difference2 = normalizedValues.filter(
      (item) => !notificationTypes.includes(item)
    );
    // Prefer newly added value; removals (like dropping "same") should not drive the action.
    let clickedOption = difference2[0] || difference1.find((item) => item !== "same");
    if (!clickedOption && normalizedValues.length > 0) {
      clickedOption = normalizedValues[normalizedValues.length - 1];
    }
    if (!clickedOption) return;

    setNewNotificationType(clickedOption);
    //if ref type is not already selected
    if (!notificationTypes.includes(clickedOption)) {
      if (documentation?.notificationInformations?.length > 0) {
        setOpenNotificationConfirmationDialog(true);
        return;
      }

      const todayYmd = formatDateToYYYYMMDD(new Date());
      if (clickedOption === "same") {
        setNotificationTypes(["same"]);
        setNotificationDate(todayYmd);
      } else if (clickedOption === "nonApplicable") {
        //confirm about removing every notification infos and keep No Referral
        setOpenNotificationConfirmationDialog(true);
      } else if (
        clickedOption === "Primary Provider" &&
        selectedPrimaryProvider
      ) {
        setNotificationTypes(["Primary Provider"]);
        setNotificationDate(todayYmd);
        useExistingProvider({
          seedNotification: true,
          notificationDateOverride: todayYmd,
        });
      } else if (
        clickedOption !== "nonApplicable" &&
        clickedOption !== "same" &&
        documentation?.referralInformations?.[0] &&
        documentation.referralInformations[0].referralType === clickedOption
      ) {
        const { dateOfNotification, methodOfNotification } =
          getNotificationCopyDocOverrides(documentation, notificationMethod);
        setNotificationTypes([clickedOption]);
        setNotificationDate(dateOfNotification);
        setnotificationMethod(methodOfNotification);
        saveNotificationInformation(
          {
            ...documentation.referralInformations[0],
            referralType: clickedOption,
          },
          clickedOption,
          false,
          undefined,
          {
            dateOfNotification,
            methodOfNotification,
          }
        );
      } else {
        //add new notification
        setNotificationTypes(
          notificationTypes.filter(
            (x) => x !== "nonApplicable" && x !== "same"
          )
        );
        setNotificationDate(todayYmd);
        setCreateNotification(true);
        setShowNotificationSlider(true);
      }
    } else {
      //confirm to remove already selected notification info
      setOpenNotificationConfirmationDialog(true);
    }
  };

  const useExistingProvider = ({
    seedReferral = false,
    seedNotification = false,
    notificationDateOverride,
  } = {}) => {
    if (!selectedPrimaryProvider) return;

    const primaryProvider = buildPrimaryProviderPayload(selectedPrimaryProvider);
    const {
      doc,
      referralTypes: docReferralTypes,
      notificationTypes: docNotificationTypes,
      referralInfos,
      notificationInfos,
    } = getDocCollections(documentation);

    const shouldSeedReferral =
      seedReferral &&
      docReferralTypes.length === 0 &&
      referralInfos.length === 0 &&
      !hasPrimaryProviderInfo(referralInfos);
    const shouldSeedNotification =
      seedNotification &&
      docNotificationTypes.length === 0 &&
      notificationInfos.length === 0 &&
      !hasPrimaryProviderInfo(notificationInfos);

    if (!shouldSeedReferral && !shouldSeedNotification) return;

    const todayYmd = notificationDateOverride || formatDateToYYYYMMDD(new Date());
    const updatedSteps = {
      currentStep,
      currentSubStep,
      documentation: {
        ...doc,
        referralType: shouldSeedReferral ? ["Primary Provider"] : docReferralTypes,
        referralInformations: shouldSeedReferral ? [primaryProvider] : referralInfos,
        notificationType: shouldSeedNotification
          ? ["Primary Provider"]
          : docNotificationTypes,
        notificationInformations: shouldSeedNotification
          ? [primaryProvider]
          : notificationInfos,
        dateOfNotification: shouldSeedNotification
          ? todayYmd
          : doc?.dateOfNotification,
        methodOfNotification: shouldSeedNotification
          ? doc?.methodOfNotification || "fax"
          : doc?.methodOfNotification,
      },
    };

    if (shouldSeedReferral) {
      setReferralTypes(["Primary Provider"]);
      setNewReferralType("Primary Provider");
    }
    if (shouldSeedNotification) {
      setNotificationTypes(["Primary Provider"]);
      setNewNotificationType("Primary Provider");
      setNotificationDate(todayYmd);
      setnotificationMethod(updatedSteps.documentation.methodOfNotification || "fax");
    }

    persistMedicationReview(updatedSteps);
  };

  useEffect(() => {
    if (!isDocHydrated || !selectedPrimaryProvider) return;

    const {
      referralTypes: docReferralTypes,
      notificationTypes: docNotificationTypes,
      referralInfos,
      notificationInfos,
    } = getDocCollections(documentation);

    const shouldAutoReferral =
      docReferralTypes.length === 0 &&
      referralInfos.length === 0 &&
      !hasPrimaryProviderInfo(referralInfos);
    const shouldAutoNotification =
      docNotificationTypes.length === 0 &&
      notificationInfos.length === 0 &&
      !hasPrimaryProviderInfo(notificationInfos);

    if (shouldAutoReferral || shouldAutoNotification) {
      useExistingProvider({
        seedReferral: shouldAutoReferral,
        seedNotification: shouldAutoNotification,
        notificationDateOverride: formatDateToYYYYMMDD(new Date()),
      });
    }
  }, [
    isDocHydrated,
    selectedPrimaryProvider,
    documentation?.referralType,
    documentation?.notificationType,
    documentation?.referralInformations,
    documentation?.notificationInformations,
  ]);

  // Hydrate local state from persisted documentation when returning to this step
  useEffect(() => {
    const {
      doc,
      referralTypes: docReferralTypes,
      notificationTypes: docNotificationTypes,
    } = getDocCollections(documentation);
    setReferralTypes(docReferralTypes);
    setNotificationTypes(docNotificationTypes);
    const { date, method } = resolveNotificationDefaults(doc, docNotificationTypes);
    setNotificationDate(date);
    setnotificationMethod(method);
    setIsDocHydrated(true);
  }, [documentation]);

  const referralInformations = documentation?.referralInformations || [];
  const notificationInformations = documentation?.notificationInformations || [];
  const isSameAsReferralSelected = notificationTypes.includes("same");
  const {
    combinedInfoCards,
    standaloneReferralInfos,
    standaloneNotificationInfos,
  } = buildReferralNotificationCardGroups({
    referralInformations,
    notificationInformations,
    isSameAsReferralSelected,
  });

  return (
    <div className="relative flex flex-col flex-1 w-full gap-4 bg-white">
      <div className="flex flex-col flex-1">
        <div className="flex flex-1 gap-4">
          <ChartNav />
          <Medications />
          <div className="flex flex-1 w-full h-[668px] overflow-y-auto scroll-smooth ">
            <div className="w-full gap-5">
              <div className="flex items-center justify-between mt-0.5 mb-0.5">
                <h4 className="text-xs-display font-bold text-teal-300">
                  Referrals & Notifications
                </h4>

                <SubNavigation />
              </div>
              <div className="border-b-[7px] border-gray-20 mb-[20px]"></div>
              <div
                className="grid grid-flow-col auto-cols-[480px] gap-6 scroll-smooth overflow-auto"
                id="life-style"
              >
                {/* referral card */}
                <div className="h-[595px] min-w-[480px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                  <div
                    className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                    style={{
                      backgroundImage: `url(/icons/texture-background-2.png)`,
                    }}
                  >
                    <h3 className="text-xl font-bold text-teal-600 ">
                      Referral Type
                    </h3>
                  </div>
                  <div className="flex flex-col gap-4 pl-5 py-5 pr-2 overflow-scroll overflow-x-hidden bg-scroll min-h-[465px]">
                    <div className="flex flex-col gap-4">
                      <p className="text-lg font-semibold text-gray-800">
                        Select Health Care Practitioner Type (Optional):
                      </p>
                      <div className="flex flex-col gap-5 pt-2 overflow-scroll overflow-x-hidden bg-scroll min-h-[465px]">
                        <div className="flex flex-col gap-2">
                          {AMRReferralTypeOptions.map((option) => {
                            return (
                              <button
                                onClick={() => {
                                  HandleReferralSelection(option.value);
                                }}
                                className={`font-regular text-body-md p-3 rounded-[4px] border-[1px] text-gray-800 
                            hover:text-gray-800 hover:bg-gray-20 hover:border-gray-6001 ${
                              referralTypes.includes(option.value)
                                ? "bg-gray-100 border-gray-600"
                                : "bg-white border-gray-100"
                            }`}
                              >
                                <p className="font-regular text-body-md flex justify-start items-start">
                                  {option.label}
                                </p>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* referral informations */}
                {standaloneReferralInfos.length > 0 &&
                  standaloneReferralInfos.map((ref) => (
                    <ReferralCard
                      clinicalServiceName={clinicalServiceName}
                      referral={ref}
                      onEdit={() => {
                        setReferralInfo(ref);
                        setCreateReferral(false);
                        setShowReferralSlider(true);
                      }}
                      onDelete={() => {
                        setNewReferralType(ref?.referralType);
                        //confirm to remove already selected referral info
                        setOpenreferralDeleteDialog(true);
                      }}
                    />
                  ))}

                {/* notification card */}
                <div
                  id="notificationOfService"
                  className={`h-[595px] min-w-[480px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col`}
                >
                  <div
                    className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                    style={{
                      backgroundImage: `url(/icons/texture-background-2.png)`,
                    }}
                  >
                    <h3 className="text-xl font-bold text-teal-600 ">
                      Notification of Service
                    </h3>
                  </div>
                  <div className="flex flex-col gap-4 pl-5 py-5 pr-2 overflow-scroll overflow-x-hidden bg-scroll min-h-[595px]">
                    <div className="flex flex-col gap-4">
                      <p className="text-lg font-semibold text-gray-800">
                        Notification Type
                      </p>
                      <CustomInput
                        options={notificationOptions}
                        closeOnSelectValues={notificationOptions.map((s) => s.value)}
                        name="reason"
                        className="col-span-12"
                        label=""
                        type="multi-dropdown"
                        value={notificationTypes}
                        onChange={(e) => {
                          HandleNotificationSelection(e);
                        }}
                      />
                    </div>
                    <div className="flex flex-col gap-4">
                      <p className="text-lg font-semibold text-gray-800">
                        Date of Notification
                      </p>
                      <CustomInput
                        name="Notification Date"
                        className="col-span-6"
                        label=""
                        type="date"
                        value={notificationDate}
                        onChange={(e) => {
                          setNotificationDate(e);
                        }}
                      />
                    </div>
                    <div className="flex flex-col gap-4">
                      <p className="text-lg font-semibold text-gray-800">
                        Method of Notification
                      </p>
                      <CustomInput
                        options={notificationMethodOptions}
                        name="reason"
                        className="col-span-12"
                        label=""
                        type="dropdown"
                        value={notificationMethod}
                        onChange={(e) => setnotificationMethod(e.value)}
                        shortMenuHeight={true}
                      />
                    </div>
                  </div>
                </div>

                {/* notification informations */}
                {combinedInfoCards.map((card) => (
                  <NotificationCard
                    key={`combined-${card.key}`}
                    notification={card.info}
                    title="Referral & Notification Information"
                    typeLabel="Referral / Notification Type"
                    typeOptions={AMRNotificationTypeOptions}
                    onEdit={() => {
                      if (card.source === "same") {
                        setReferralInfo(card.info);
                        setCreateReferral(false);
                        setShowReferralSlider(true);
                      } else {
                        setNotificationInfo(card.info);
                        setCreateNotification(false);
                        setShowNotificationSlider(true);
                      }
                    }}
                    onDelete={() => {
                      if (card.source === "same") {
                        setNewReferralType(card.info?.referralType);
                        setOpenreferralDeleteDialog(true);
                      } else {
                        setNewNotificationType(card.info?.referralType);
                        setOpenNotificationDeleteDialog(true);
                      }
                    }}
                  />
                ))}
                {standaloneNotificationInfos.length > 0 &&
                  standaloneNotificationInfos.map((not) => (
                    <NotificationCard
                      notification={not}
                      typeOptions={AMRNotificationTypeOptions}
                      onEdit={() => {
                        setNotificationInfo(not);
                        setCreateNotification(false);
                        setShowNotificationSlider(true);
                      }}
                      onDelete={() => {
                        setNewNotificationType(not?.referralType);
                        //confirm to remove already selected notification info
                        setOpenNotificationDeleteDialog(true);
                      }}
                    />
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between space-x-4 rounded">
        <div className="w-full ">
          <Steps position="bottom" stepsList={clinicalServiceSteps} />
        </div>
        <div className="flex items-center gap-3 ">
          <Button
            type="primary-outline"
            text="Back"
            className="w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={() => {
              onPrevious();
            }}
          />
          <Button
            type="primary"
              text="Next"
              className={`w-[200px] max-h-[52px] !rounded-[30px] `}
              disabled={!subStepComplete}
              onClick={() => {
                onNext();
              }}
          />
        </div>
      </div>

      {/* create referral form */}
      <ReferralCreate
        create={createReferral}
        type={newReferralType}
        updateType={(val) => {
          if (!referralTypes.includes(val))
            setReferralTypes([...referralTypes, val]);
        }}
        open={showReferralSlider}
        existingReferral={referralInfo}
        onClose={(value) => {
          setCreateReferral(true);
          setShowReferralSlider(false);
        }}
        onSave={(info) => {
          saveReferralInformation(info, false);
          const notType = notificationTypes[0];
          if (notType === "same") {
            saveNotificationInformation(info, "same", false, "same");
          } else if (
            notType &&
            notType === info.referralType &&
            notType !== "nonApplicable"
          ) {
            saveNotificationInformation(
              { ...info, referralType: info.referralType },
              info.referralType,
              false,
              undefined,
              getNotificationCopyDocOverrides(documentation, notificationMethod)
            );
          }
          setCreateReferral(true);
          setShowReferralSlider(false);
        }}
        onUpdate={(info) => {
          saveReferralInformation(info, true);
          const notType = notificationTypes[0];
          if (notType === "same") {
            saveNotificationInformation(info, "same", true, "same");
          } else if (
            notType &&
            notType === info.referralType &&
            notType !== "nonApplicable"
          ) {
            saveNotificationInformation(
              { ...info, referralType: info.referralType },
              info.referralType,
              true,
              undefined,
              getNotificationCopyDocOverrides(documentation, notificationMethod)
            );
          }
          setCreateReferral(true);
          setShowReferralSlider(false);
        }}
        referralOptions={AMRReferralTypeOptions}
      />

      {/* create notification form */}
      <NotificationCreate
        create={createNotification}
        type={newNotificationType}
        updateType={(val) => {
          if (!notificationTypes.includes(val))
            setNotificationTypes([...notificationTypes, val]);
        }}
        open={showNotificationSlider}
        existingNotification={notificationInfo}
        onClose={(value) => {
          setCreateNotification(true);
          setShowNotificationSlider(false);
        }}
        onSave={(info) => {
          saveNotificationInformation(info, info.referralType, false);
          setCreateNotification(true);
          setShowNotificationSlider(false);
        }}
        onUpdate={(info) => {
          saveNotificationInformation(info, info.referralType, true);
          setCreateNotification(true);
          setShowNotificationSlider(false);
        }}
        notificationOptions={AMRNotificationTypeOptions}
      />

      {/* referral-confirmation removed — behavior executes immediately on selection */}
      {/* delete referral dialog */}
      <ConfirmationModalNew
        open={openreferralDeleteDialog}
        title="Delete Referral Type"
        className="!min-w-[555px]"
        confirmText={"Delete"}
        cancelText="Cancel"
        content={() => (
          <div>
            <div className="flex justify-between">
              {"Do you wish to delete the Referral Type?"}
            </div>
          </div>
        )}
        onCross={() => setOpenreferralDeleteDialog(false)}
        handleClose={() => setOpenreferralDeleteDialog(false)}
        confirmAction={() => {
          if (newReferralType !== "No Referral" && referralTypes?.length > 1) {
            setReferralTypes(
              referralTypes.filter((x) => x !== newReferralType)
            );
            saveReferralInformation(null, false, newReferralType);
          } else {
            setReferralTypes(["No Referral"]);
            saveReferralInformation(null);
          }
          setOpenreferralDeleteDialog(false);
        }}
      />

      {/* confirm notification update dialog */}
      <ConfirmationModalNew
        open={openNotificationConfirmationDialog}
        title="Confirm Notification Update"
        className="!min-w-[555px]"
        confirmText="Update Notification"
        cancelText="Cancel"
        content={() => (
          <div>
            <div className="flex justify-between">
              Do you wish to change the Notification Type?
            </div>
          </div>
        )}
        onCross={() => setOpenNotificationConfirmationDialog(false)}
        handleClose={() => setOpenNotificationConfirmationDialog(false)}
        confirmAction={() => {
          const {
            dateOfNotification: todayYmd,
            methodOfNotification: resolvedMethod,
          } = getNotificationCopyDocOverrides(documentation, notificationMethod);
          setNotificationTypes([newNotificationType]);
          setNotificationDate(todayYmd);

          if (newNotificationType === "same") {
            const referralInfo = documentation?.referralInformations?.[0] || null;
            if (referralInfo) {
              saveNotificationInformation(
                referralInfo,
                newNotificationType,
                false,
                "same",
                {
                  dateOfNotification: todayYmd,
                  methodOfNotification: resolvedMethod,
                }
              );
            } else {
              saveNotificationInformation(null, "nonApplicable", false, undefined, {
                dateOfNotification: null,
                methodOfNotification: null,
              });
            }
          } else if (newNotificationType === "nonApplicable") {
            saveNotificationInformation(null, "nonApplicable", false, undefined, {
              dateOfNotification: null,
              methodOfNotification: null,
            });
          } else if (
            newNotificationType === "Primary Provider" &&
            selectedPrimaryProvider
          ) {
            useExistingProvider({
              seedNotification: true,
              notificationDateOverride: todayYmd,
            });
          } else if (
            documentation?.referralInformations?.[0]?.referralType ===
            newNotificationType
          ) {
            saveNotificationInformation(
              {
                ...documentation.referralInformations[0],
                referralType: newNotificationType,
              },
              newNotificationType,
              false,
              undefined,
              {
                dateOfNotification: todayYmd,
                methodOfNotification: resolvedMethod,
              }
            );
          } else {
            setCreateNotification(true);
            setShowNotificationSlider(true);
          }
          setOpenNotificationConfirmationDialog(false);
        }}
      />
      {/* delete notification dialog */}
      <ConfirmationModalNew
        open={openNotificationDeleteDialog}
        title="Delete Notification Type"
        className="!min-w-[555px]"
        confirmText="Delete"
        cancelText="Cancel"
        content={() => (
          <div>
            <div className="flex justify-between">
              Do you wish to delete the Notification Type?
            </div>
          </div>
        )}
        onCross={() => setOpenNotificationDeleteDialog(false)}
        handleClose={() => setOpenNotificationDeleteDialog(false)}
        confirmAction={() => {
          setNotificationTypes(["nonApplicable"]);
          saveNotificationInformation(null, "nonApplicable", false, undefined, {
            dateOfNotification: null,
            methodOfNotification: null,
          });
          setOpenNotificationDeleteDialog(false);
        }}
      />
    </div>
  );
};

export default ReferralsAMR;
